import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { EntregaDoPedidoBloco } from "./EntregaDoPedidoBloco";
import { MenuDeImpressao } from "./MenuDeImpressao";
import { codigoDoPedido } from "@/lib/codigo-do-pedido";
import { useState } from "react";
import { Check, Download, PenLine, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@bookdental/ui";
import { valorDaTaxaDeUrgencia, type ProsthesisStatus } from "@bookdental/schemas";
import { useTaxaDeUrgencia } from "@/lib/taxa-de-urgencia";
import { EditarServicoDialog } from "./EditarServicoDialog";
import { RequestReworkDialog, type ReworkTarget } from "@/components/prosthesis/RequestReworkDialog";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";
import { usePermissao } from "@/lib/use-permissao";
import { STATUS_META, STATUS_ORDER, STATUS_TERMINAIS, formatBRL, formatDate } from "@/components/prosthesis/prosthesis-meta";
import { ProsthesisTeethDisplay } from "@/components/prosthesis/ProsthesisTeethDisplay";
import { SentChannelsField } from "@/components/prosthesis/SentChannelsField";
import type { ProsthesisServiceRow } from "@/components/prosthesis/types";
import { EntityFilesPanel } from "@/components/files/EntityFilesPanel";
import { ColorSelect } from "./ColorSelect";
import { orderIsPriced, orderTotalCents, type IncomingOrderRow } from "./production-types";
import { useAceitarPedido } from "./use-aceitar-pedido";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";
import { AcrescimosDoPedido } from "@/components/prosthesis/AcrescimosDoPedido";
import { valorDoServicoEmTexto } from "@/components/prosthesis/valor-do-pedido";

async function downloadAttachment(utils: ReturnType<typeof trpc.useUtils>, fileId: string) {
  try {
    const { url } = await utils.file.getDownloadUrl.fetch({ fileId });
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    toast.error("Não foi possível baixar o arquivo.");
  }
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">{label}</p>
      <p className="text-sm text-zinc-900 dark:text-foreground">{value}</p>
    </div>
  );
}

function IdField({ label, id, className }: { label: string; id: string; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-400 dark:text-muted-foreground/70">{label}</p>
      <p className="select-all break-all font-mono text-[10px] text-zinc-400 dark:text-muted-foreground/70">{id}</p>
    </div>
  );
}

function ServiceDetailRow({
  service,
  onPropor,
  onRefazer,
}: {
  service: ProsthesisServiceRow;
  onPropor: () => void;
  onRefazer: () => void;
}) {
  const utils = trpc.useUtils();
  const podeGerenciar = usePermissao("production.manage");
  const setStatus = trpc.prosthesis.incoming.setStatus.useMutation({
    onSuccess: () => utils.prosthesis.incoming.list.invalidate(),
  });

  return (
    <div className="rounded-lg border border-zinc-200 p-3 dark:border-border">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
          {service.serviceCategory.label}
        </p>
        {podeGerenciar ? (
          <ColorSelect
            className="justify-self-center"
            value={service.status}
            disabled={setStatus.isPending}
            onChange={(v) => setStatus.mutate({ serviceId: service.id, status: v as ProsthesisStatus })}
            options={STATUS_ORDER.map((s) => ({
              value: s,
              label: STATUS_META[s].label,
              bg: STATUS_META[s].bg,
              color: STATUS_META[s].color,
            }))}
          />
        ) : (
          <span
            className="justify-self-center rounded px-1.5 py-0.5 text-[11px] font-semibold"
            style={{ backgroundColor: STATUS_META[service.status].bg, color: STATUS_META[service.status].color }}
          >
            {STATUS_META[service.status].label}
          </span>
        )}
        {service.priceCents != null && (
          <span className="shrink-0 justify-self-end text-sm font-medium tabular-nums text-zinc-700 dark:text-foreground/80">
            {valorDoServicoEmTexto(service)}
          </span>
        )}
      </div>

      <div className="mt-2 flex flex-col gap-6 sm:flex-row">
        <div className="w-full shrink-0 sm:w-[130px]">
          <ProsthesisTeethDisplay teeth={service.teeth} />
        </div>
        <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-2">
          <Field label="Serviço" value={service.serviceCategory.label} />
          <Field label="Dentes" value={service.teeth || "—"} />
          <Field label="Material" value={rotuloDoMaterialDoServico(service) ?? "—"} />
          <Field label="Cor" value={service.shadeOption?.code ?? "—"} />
          <Field label="Observações" value={service.notes || "—"} className="col-span-2" />
          <IdField label="ID do serviço" id={service.id} className="col-span-2" />
        </div>
      </div>

      {/* Mesma regra do diálogo do serviço, e pelos mesmos motivos: concluído troca Editar por
          Refazer (editar o entregue reescreveria o passado), cancelado e recusado não têm nenhum
          dos dois. Cada linha aqui já é um serviço — as duas ações são por serviço, não pelo
          pedido. */}
      {podeGerenciar && !STATUS_TERMINAIS.includes(service.status) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onPropor}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-500/10"
          >
            <PenLine className="h-3.5 w-3.5" />
            Editar serviço
          </button>
        </div>
      )}

      {podeGerenciar && service.status === "CONCLUIDO" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onRefazer}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-950/20"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Refazer serviço
          </button>
        </div>
      )}

      {service.attachmentFile && (
        <button
          type="button"
          onClick={() => downloadAttachment(utils, service.attachmentFile!.id)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-border dark:text-primary dark:hover:bg-primary/10"
        >
          <Download className="h-3.5 w-3.5" />
          Baixar arquivo anexado
        </button>
      )}
    </div>
  );
}

export function ProductionOrderDetailDialog({
  order,
  onClose,
}: {
  order: IncomingOrderRow | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  // A proposta nasce de um SERVIÇO, não do pedido — é nele que a decisão acontece.
  const [servicoAlvo, setServicoAlvo] = useState<any | null>(null);
  const [refazendo, setRefazendo] = useState<ReworkTarget>(null);
  const total = order && orderIsPriced(order) ? orderTotalCents(order) : null;
  const podeLancarAcrescimo = usePermissao("prices.manage");
  const taxaDeUrgencia = useTaxaDeUrgencia();

  const acceptOrder = useAceitarPedido();

  const declineOrder = trpc.prosthesis.incoming.declineOrder.useMutation({
    onSuccess: () => {
      toast.success("Pedido recusado.");
      utils.prosthesis.incoming.list.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao recusar pedido."),
  });

  function handleAccept() {
    if (order) void acceptOrder.aceitar(order);
  }

  async function handleDecline() {
    if (!order) return;
    const ok = await confirm({
      title: "Recusar pedido?",
      description: `O pedido de "${order.patientName}" será marcado como recusado. Essa ação não tem volta fácil.`,
      confirmText: "Recusar pedido",
      cancelText: "Voltar",
      destructive: true,
    });
    if (ok) declineOrder.mutate({ orderId: order.id });
  }

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto dark:bg-card">
        {order && (
          <>
            <DialogHeader>
              {/* pr-8: o X de fechar mora no canto; o Imprimir fica antes dele, nunca por baixo. */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pr-8">
                <DialogTitle ajuda={AJUDA_PRODUCAO.detalheDoPedido}>{order.patientName}</DialogTitle>
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase"
                  style={{ backgroundColor: STATUS_META[order.status].bg, color: STATUS_META[order.status].color }}
                  title="Status geral do pedido (calculado a partir dos serviços)"
                >
                  {STATUS_META[order.status].label}
                </span>
                <div className="ml-auto">
                  <MenuDeImpressao order={order} />
                </div>
              </div>
              <DialogDescription>
                Pedido nº {codigoDoPedido(order)} · {order.organization.name}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="detalhes">
              <TabsList className="grid h-auto w-full grid-cols-2 bg-zinc-100 p-1 dark:bg-muted">
                <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
                <TabsTrigger value="arquivos">Arquivos</TabsTrigger>
              </TabsList>

              <TabsContent value="detalhes" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 rounded-lg bg-zinc-50 p-3 text-sm dark:bg-background/50 sm:grid-cols-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Dentista</p>
                    <p className="font-medium text-zinc-900 dark:text-foreground">{order.dentistName ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Data do pedido</p>
                    <p className="font-medium text-zinc-900 dark:text-foreground">{formatDate(order.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Prazo</p>
                    <p className="font-medium text-zinc-900 dark:text-foreground">{formatDate(order.deadline)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Valor total</p>
                    <p className="font-medium text-zinc-900 dark:text-foreground">{formatBRL(total)}</p>
                  </div>
                  <IdField label="ID do pedido" id={order.id} className="col-span-2 sm:col-span-4" />
                </div>

                <EntregaDoPedidoBloco order={order} />

                {order.description && (
                  <div>
                    <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                      Observações gerais
                    </p>
                    <p className="text-sm text-zinc-700 dark:text-foreground/80">{order.description}</p>
                  </div>
                )}

                <SentChannelsField orderId={order.id} sentChannels={order.sentChannels} />

                <div>
                  <p className="mb-2 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                    Serviços ({order.services.length})
                  </p>
                  <div className="flex flex-col gap-2">
                    {order.services.map((s) => (
                      <ServiceDetailRow
                        key={s.id}
                        service={s}
                        onPropor={() => setServicoAlvo(s)}
                        onRefazer={() =>
                          setRefazendo({
                            serviceId: s.id,
                            serviceLabel: s.serviceCategory.label,
                            patientName: order.patientName,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>

                <AcrescimosDoPedido
                  orderId={order.id}
                  acrescimos={order.acrescimos ?? []}
                  podeEditar={podeLancarAcrescimo && order.status !== "CANCELADO" && order.status !== "RECUSADO"}
                  taxaDeUrgenciaCents={taxaDeUrgencia ? valorDaTaxaDeUrgencia(taxaDeUrgencia, order.services ?? []) : undefined}
                />

                {order.status === "AGUARDANDO_CONFIRMACAO" && (
                  <div className="flex justify-end gap-2 border-t border-zinc-100 pt-4 dark:border-border">
                    <button
                      type="button"
                      disabled={declineOrder.isPending || acceptOrder.isPending}
                      onClick={handleDecline}
                      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 px-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
                    >
                      <X className="h-4 w-4" />
                      Recusar
                    </button>
                    <button
                      type="button"
                      disabled={acceptOrder.isPending || declineOrder.isPending}
                      onClick={handleAccept}
                      className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60"
                    >
                      <Check className="h-4 w-4" />
                      Aceitar
                    </button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="arquivos" className="mt-4">
                <EntityFilesPanel relatedType="PROSTHESIS_ORDER" relatedId={order.id} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
      {order && (
        <>
          <EditarServicoDialog
            aberto={!!servicoAlvo}
            servico={servicoAlvo}
            onFechar={() => setServicoAlvo(null)}
          />
          <RequestReworkDialog target={refazendo} lado="laboratorio" onClose={() => setRefazendo(null)} />
        </>
      )}
    </Dialog>
  );
}
